<template>
    <div class="pcc-card">
        <div class="pcc-header">
            <div class="pcc-title">
                <span class="pcc-drag-handle" :title="$t('settings.dragHandleTitle')">≡</span>
                <span class="pcc-title-text">{{ getContentName(content) }}</span>
            </div>
            <div class="pcc-actions">
                <SupsisButton
                    v-if="canCreateItems"
                    icon
                    square
                    small
                    v-tippy="{ content: altLabel }"
                    @click="$emit('add-to-title', content, contentTypes.ALT)"
                >
                    <md-icon>keyboard</md-icon>
                </SupsisButton>
                <SupsisButton
                    v-if="canCreateItems"
                    icon
                    square
                    small
                    v-tippy="{ content: tabLabel }"
                    @click="$emit('add-to-title', content, contentTypes.TAB)"
                >
                    <md-icon>space_bar</md-icon>
                </SupsisButton>
                <SupsisButton
                    v-if="canCreateItems"
                    icon
                    square
                    small
                    v-tippy="{ content: hashLabel }"
                    @click="$emit('add-to-title', content, contentTypes.HASH)"
                >
                    <md-icon>tag</md-icon>
                </SupsisButton>
                <SupsisButton icon square small v-tippy="{ content: $t('message.edit') }" @click="$emit('edit', content._id)">
                    <md-icon>edit</md-icon>
                </SupsisButton>
                <SupsisButton
                    v-if="canDeleteCategory"
                    icon
                    square
                    small
                    classNames="pcc-delete-btn"
                    v-tippy="{ content: $t('message.deleteField') }"
                    @click="$emit('remove', content._id)"
                >
                    <md-icon>delete</md-icon>
                </SupsisButton>
            </div>
        </div>

        <draggable
            :id="content._id"
            :key="content._id"
            :move="move"
            v-model="content.children"
            class="pcc-list"
            group="contents"
            :options="{ handle: '.pci-drag' }"
            :disabled="disabled"
            @end="$emit('move-end', $event)"
        >
            <PreparedContentsItemRow
                v-for="child in content.children || []"
                :key="child._id"
                :id="child._id"
                :child="child"
                :is-mac="isMac"
                :is-dark-mode="isDarkMode"
                :can-delete="canDeleteItem"
                :get-content-name="getContentName"
                @edit="handleEdit"
                @remove="handleRemove"
            />
        </draggable>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import SupsisButton from "../../../components/SupsisButton.vue";
import PreparedContentsItemRow from "./PreparedContentsItemRow.vue";

export default {
    name: "PreparedContentsCategoryCard",
    components: {
        draggable,
        PreparedContentsItemRow,
        SupsisButton,
    },
    props: {
        content: { type: Object, required: true },
        contentTypes: { type: Object, required: true },
        isMac: Boolean,
        isDarkMode: Boolean,
        move: { type: Function, required: true },
        getContentName: { type: Function, required: true },
        getChildren: { type: Function, required: true },
        canCreateItems: { type: Boolean, default: true },
        canDeleteItem: Boolean,
        canDeleteCategory: Boolean,
        altLabel: String,
        tabLabel: String,
        hashLabel: String,
        disabled: Boolean,
    },
    methods: {
        handleEdit(id, isChild) {
            this.$emit("edit", id, isChild);
        },
        handleRemove(id) {
            this.$emit("remove", id);
        },
    },
};
</script>

<style lang="scss" scoped>
.pcc-card {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    border: 1px solid var(--color-border-default);
    background: #ffffff;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
}

.pcc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-default);
    background: var(--color-bg-neutral-default);
}

.pcc-title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.pcc-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    cursor: grab;
    user-select: none;
    font-size: 18px;
    line-height: 1;
    color: var(--color-text-subtle);
    background: var(--color-bg-neutral-default);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    transition: all 0.2s;

    &:hover {
        color: var(--color-text-strong-default);
        border-color: #22272d;
        background: #ffffff;
    }

    &:active {
        cursor: grabbing;
        transform: scale(0.95);
    }
}

.pcc-title-text {
    font-size: 14px;
    font-weight: 700;
    color: var(--color-text-strong-default);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pcc-actions {
    display: flex;
    align-items: center;
    gap: 4px;

    ::v-deep .pcc-delete-btn {
        border: none !important;
        background: transparent !important;
        color: #ef4444 !important;

        &:hover {
            background: rgba(239, 68, 68, 0.1) !important;
        }
    }
}

.pcc-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    min-height: 120px;
    max-height: 400px;
    overflow-y: auto;
}
</style>
